import { Box, Button, Card, Stack, Text } from "@sanity/ui";
import { Code } from "@sanity/ui/code";
import { CheckmarkIcon } from "@sanity/icons/Checkmark";
import { CloseIcon } from "@sanity/icons/Close";
import { WarningOutlineIcon } from "@sanity/icons/WarningOutline";
import React from "react";

type Props = {
  converted: number;
  convertedPaths: string[];
  languageFixed: number;
  languageFixedPaths: string[];
  unsupportedLanguages: string[];
  onConfirm: () => void;
  onClose: () => void;
};

const PathList: React.FC<{ paths: string[] }> = ({ paths }) => (
  <Card padding={3} radius={2} tone="caution">
    <Stack gap={2}>
      {paths.map((p) => (
        <Code key={p} size={1}>
          {p}
        </Code>
      ))}
    </Stack>
  </Card>
);

const FixMultilingualDialog: React.FC<Props> = ({
  converted,
  convertedPaths,
  languageFixed,
  languageFixedPaths,
  unsupportedLanguages,
  onConfirm,
  onClose
}) => {
  if (converted === 0 && languageFixed === 0) {
    return (
      <Box padding={4}>
        <Stack gap={4}>
          <Text>
            Document schema is already up to date. No fields to convert.
          </Text>
          <Button
            tone="default"
            icon={CloseIcon}
            text="Close"
            onClick={onClose}
          />
        </Stack>
      </Box>
    );
  }

  return (
    <Box padding={4}>
      <Stack gap={4}>
        {converted > 0 && (
          <>
            <Text>
              Found <strong>{converted}</strong> field
              {converted !== 1 ? "s" : ""} with legacy multilingual format that
              will be converted to multilingual array:
            </Text>
            <PathList paths={convertedPaths} />
          </>
        )}

        {languageFixed > 0 && (
          <>
            <Text>
              Found <strong>{languageFixed}</strong> translation item
              {languageFixed !== 1 ? "s" : ""} still using the old (v4) format,
              where the language is stored in the item key. The{" "}
              <code>language</code> field will be filled in:
            </Text>
            <PathList paths={languageFixedPaths} />
          </>
        )}

        {unsupportedLanguages.length > 0 && (
          <Card padding={3} radius={2} tone="critical">
            <Stack gap={2}>
              <Text size={1}>
                <WarningOutlineIcon /> Some items use language codes that are no
                longer configured: <strong>{unsupportedLanguages.join(", ")}</strong>
                . Their content is kept, but the fields will stay invalid until
                you remove those items.
              </Text>
            </Stack>
          </Card>
        )}

        <Text size={1} muted>
          Only the draft will be updated. The published document remains
          unchanged until you explicitly publish.
        </Text>
        <Stack gap={2}>
          <Button
            tone="positive"
            icon={CheckmarkIcon}
            text="Fix & Save Draft"
            onClick={onConfirm}
          />
          <Button
            tone="default"
            mode="ghost"
            icon={CloseIcon}
            text="Cancel"
            onClick={onClose}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default FixMultilingualDialog;
